const { compose, flatten, uniq } = require('ramda');

const {
  BUSINESS_LEGAL_STATUS,
  COMPANY_REGISTRATION,
  DRIVER_LICENSE,
  DRIVER_LICENSE_HOST,
  FOURTHLINE_GENERIC_ID_DOCUMENT,
  ID_RECTO,
  ID_RECTO_HOST,
  ID_VERSO,
  ID_VERSO_HOST,
  PASSPORT,
  PASSPORT_HOST,
  PROOF_OF_ACCOMMODATION,
  PROOF_OF_ADDRESS,
  PROOF_OF_ADDRESS_HOST,
  RESIDENCE_PERMIT_RECTO,
  RESIDENCE_PERMIT_RECTO_HOST,
  RESIDENCE_PERMIT_VERSO,
  RESIDENCE_PERMIT_VERSO_HOST,
} = require('./kycDocs');

const uniqFlatten = compose(uniq, flatten);

const KYC_STATUSES = {
  MISSING: 'MISSING',
  NONE: 'NONE',
  PENDING: 'PENDING',
  PREVALIDATED: 'PREVALIDATED',
  REFUSED: 'REFUSED',
  VALIDATED: 'VALIDATED',
};

const KYC_CATEGORIES = {
  ADDRESS: 'ADDRESS',
  ADDRESS_HOST: 'ADDRESS_HOST',
  ID: 'ID',
  ID_HOST: 'ID_HOST',
  KBIS: 'KBIS',
  PROOF_OF_ACCOMMODATION: 'PROOF_OF_ACCOMMODATION',
  STATUS: 'STATUS',
};

const kycDocumentByCategories = {
  ADDRESS: [
    [PROOF_OF_ADDRESS],
    [
      PROOF_OF_ADDRESS_HOST,
      PROOF_OF_ACCOMMODATION,
      ID_RECTO_HOST,
      ID_VERSO_HOST,
    ],
    [PROOF_OF_ADDRESS_HOST, PROOF_OF_ACCOMMODATION, PASSPORT_HOST],
    [PROOF_OF_ADDRESS_HOST, PROOF_OF_ACCOMMODATION, DRIVER_LICENSE_HOST],
    [
      PROOF_OF_ADDRESS_HOST,
      PROOF_OF_ACCOMMODATION,
      RESIDENCE_PERMIT_RECTO_HOST,
      RESIDENCE_PERMIT_VERSO_HOST,
    ],
  ],
  ADDRESS_HOST: [[PROOF_OF_ADDRESS_HOST]],
  ID: [
    [ID_RECTO, ID_VERSO],
    [PASSPORT],
    [DRIVER_LICENSE],
    [RESIDENCE_PERMIT_RECTO, RESIDENCE_PERMIT_VERSO],
    [FOURTHLINE_GENERIC_ID_DOCUMENT],
  ],
  ID_HOST: [
    [ID_RECTO_HOST, ID_VERSO_HOST],
    [PASSPORT_HOST],
    [DRIVER_LICENSE_HOST],
    [RESIDENCE_PERMIT_RECTO_HOST, RESIDENCE_PERMIT_VERSO_HOST],
  ],
  KBIS: [[COMPANY_REGISTRATION]],
  PROOF_OF_ACCOMMODATION: [[PROOF_OF_ACCOMMODATION]],
  STATUS: [[BUSINESS_LEGAL_STATUS]],
};

const kycDocumentTypesByCategory = {
  ADDRESS: uniqFlatten(kycDocumentByCategories.ADDRESS),
  ID: uniqFlatten(kycDocumentByCategories.ID),
};

const getKycDocStatus = ({ category, documents }) => {
  if (!documents || !documents.length) {
    return KYC_STATUSES.NONE;
  }

  const requiredDocs = kycDocumentByCategories[category];

  const isRefused = requiredDocs.some((DOC_TYPES) =>
    DOC_TYPES.some((DOC_TYPE) =>
      documents.find(
        (doc) => doc.type === DOC_TYPE && doc.status === KYC_STATUSES.REFUSED,
      ),
    ),
  );

  if (isRefused) {
    return KYC_STATUSES.REFUSED;
  }

  const isPending = requiredDocs.some(
    // All docs for this category have been added
    (DOC_TYPES) =>
      DOC_TYPES.every((DOC_TYPE) =>
        documents.find(
          ({ status, type }) =>
            type === DOC_TYPE &&
            [
              KYC_STATUSES.VALIDATED,
              KYC_STATUSES.PENDING,
              KYC_STATUSES.PREVALIDATED,
            ].includes(status),
        ),
      ) &&
      // But some aren't validated yet
      DOC_TYPES.some((DOC_TYPE) =>
        documents.find(
          ({ status, type }) =>
            type === DOC_TYPE && status !== KYC_STATUSES.VALIDATED,
        ),
      ),
  );

  if (isPending) {
    return KYC_STATUSES.PENDING;
  }

  const isValidated = requiredDocs.some((DOC_TYPES) =>
    DOC_TYPES.every((DOC_TYPE) =>
      documents.find(
        (doc) => doc.type === DOC_TYPE && doc.status === KYC_STATUSES.VALIDATED,
      ),
    ),
  );

  if (isValidated) {
    return KYC_STATUSES.VALIDATED;
  }

  const isMissing = requiredDocs.some(
    // Some document have been submitted
    (DOC_TYPES) =>
      DOC_TYPES.some((DOC_TYPE) =>
        documents.find(
          ({ type }) => type === DOC_TYPE,
          KYC_STATUSES.VALIDATED,
          KYC_STATUSES.PENDING,
          KYC_STATUSES.PREVALIDATED,
        ),
      ) &&
      // And at least one is not listed or listed missing
      DOC_TYPES.some((DOC_TYPE) => {
        const document = documents.find(({ type }) => type === DOC_TYPE);

        return !document || document.status === KYC_STATUSES.MISSING;
      }),
  );

  if (isMissing) {
    return KYC_STATUSES.MISSING;
  }

  return KYC_STATUSES.NONE;
};

module.exports = {
  getKycDocStatus,
  KYC_CATEGORIES,
  KYC_STATUSES,
  kycDocumentByCategories,
  kycDocumentTypesByCategory,
};
